import { useState } from 'react';
import { Link } from 'react-router-dom';

import { cx } from '../lib/format.js';

/**
 * ui.jsx — Small presentational primitives shared across pages.
 *
 * Grouped in one module because each is only a few lines; splitting them into
 * separate files would add navigation cost without adding clarity.
 */

// ─── Button ───────────────────────────────────────────────────────────────────

const BUTTON_VARIANTS = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-soft',
  secondary: 'bg-white text-ink-800 border border-ink-200 hover:bg-ink-50 active:bg-ink-100',
  ghost: 'text-ink-700 hover:bg-ink-100 active:bg-ink-200',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  growth: 'bg-growth-600 text-white hover:bg-growth-700 active:bg-growth-800 shadow-soft',
};

const BUTTON_SIZES = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-13 px-7 text-base gap-2.5',
};

/**
 * A button that can render as a `<button>`, a router `<Link>` or an `<a>`,
 * depending on which props it receives — so visual style never dictates whether
 * something is semantically a control or a navigation.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  to,
  href,
  className,
  children,
  loading = false,
  disabled,
  ...rest
}) {
  const classes = cx(
    'inline-flex items-center justify-center rounded-xl font-semibold transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-55',
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    className,
  );

  const content = (
    <>
      {loading && <Spinner className="size-4" />}
      {children}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {content}
    </button>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────

const BADGE_TONES = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200',
  growth: 'bg-growth-50 text-growth-700 ring-growth-200',
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
};

export function Badge({ tone = 'neutral', className, children }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

/**
 * Loading placeholder. `aria-hidden` because a screen reader should hear the
 * live-region "Loading" announcement instead of a pile of empty boxes.
 */
export function Skeleton({ className }) {
  return <div className={cx('skeleton', className)} aria-hidden="true" />;
}

// ─── States ───────────────────────────────────────────────────────────────────

export function ErrorState({ title = 'Something went wrong', message, onRetry, className }) {
  return (
    <div role="alert" className={cx('surface flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span className="grid size-11 place-items-center rounded-full bg-red-50 text-red-700" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
      </span>
      <div>
        <h2 className="font-semibold text-ink-900">{title}</h2>
        {message && <p className="mt-1 max-w-md text-sm text-ink-600">{message}</p>}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ title, message, action, className }) {
  return (
    <div className={cx('surface flex flex-col items-center gap-3 px-6 py-16 text-center', className)}>
      <span className="grid size-11 place-items-center rounded-full bg-ink-100 text-ink-500" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </span>
      <div>
        <h2 className="font-semibold text-ink-900">{title}</h2>
        {message && <p className="mt-1 max-w-md text-sm text-ink-600">{message}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Image with fallback ──────────────────────────────────────────────────────

/**
 * Product image that fades in once decoded and falls back to a neutral device
 * glyph if the remote host fails, so a dead URL degrades to a tidy tile rather
 * than a broken-image icon.
 *
 * `key={src}` on the <img> resets the load/error state when the caller switches
 * variant image, which would otherwise leave a previously failed image stuck in
 * its fallback.
 */
export function ProductImage({ src, alt, className, imgClassName, priority = false }) {
  // Load status is tracked alongside the src it belongs to. When the caller
  // switches image, the mismatch is corrected during render — React's documented
  // way to reset state on a prop change, and cheaper than an effect, which would
  // paint one frame with the previous image's status before correcting it.
  const [tracked, setTracked] = useState({ src, status: src ? 'loading' : 'error' });

  if (tracked.src !== src) {
    setTracked({ src, status: src ? 'loading' : 'error' });
  }

  const status = tracked.status;
  const setStatus = (next) => setTracked({ src, status: next });

  return (
    <div className={cx('relative overflow-hidden bg-ink-100', className)}>
      {src && status !== 'error' && (
        <img
          key={src}
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
          className={cx(
            'size-full object-cover transition-opacity duration-500',
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
            imgClassName,
          )}
        />
      )}

      {status !== 'loaded' && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-ink-300" aria-hidden="true">
          <svg viewBox="0 0 24 24" className="size-10" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="5" y="2" width="14" height="20" rx="3" />
            <path d="M10 19h4" />
          </svg>
        </div>
      )}
    </div>
  );
}

// ─── Rating ───────────────────────────────────────────────────────────────────

export function Rating({ value, count, className }) {
  if (value === null || value === undefined) return null;
  return (
    <span className={cx('inline-flex items-center gap-1.5 text-sm', className)}>
      <span className="inline-flex items-center gap-0.5 text-amber-500" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
          <path d="m12 17.3-6.2 3.3 1.2-6.9-5-4.9 6.9-1L12 1.5l3.1 6.3 6.9 1-5 4.9 1.2 6.9z" />
        </svg>
      </span>
      <span className="font-semibold text-ink-900">{Number(value).toFixed(1)}</span>
      {count ? <span className="text-ink-500">({new Intl.NumberFormat('en-IN').format(count)})</span> : null}
      <span className="sr-only">out of 5</span>
    </span>
  );
}
